// client/src/coms/views/canvas_parts/party/PartyResults.js
// End of a party game: winner celebration, podium, standings, personal stats and the full gallery, with
// rematch / change game / leave. Also the saved-result page that the "share" link opens.
import React, { useEffect, useMemo, useState } from 'react';
import UserAvatar from '../../games_parts/UserAvatar';
import { GameGallery } from './PartyReveals';
import { createDrawingStore, partyApi } from './usePartySession';
import { RESULT_TITLES } from './partyMeta';

function usePrefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
}

function Confetti() {
  const reduced = usePrefersReducedMotion();
  const pieces = useMemo(() => Array.from({ length: reduced ? 0 : 36 }, (_, k) => ({
    left: (k * 37) % 100,
    delay: (k % 12) * 0.12,
    hue: (k * 47) % 360,
    size: 6 + (k % 4) * 2,
  })), [reduced]);
  return (
    <div className="party-confetti" aria-hidden="true">
      {pieces.map((piece, k) => <i key={k} style={{ left: `${piece.left}%`, animationDelay: `${piece.delay}s`, background: `hsl(${piece.hue} 85% 60%)`, width: piece.size, height: piece.size * 1.6 }} />)}
    </div>
  );
}

export function resultLink(resultId) {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/canvas?partyResult=${encodeURIComponent(resultId)}`;
}

export function Podium({ standings, winners, teams }) {
  const top = standings.slice(0, 3);
  const order = top.length === 3 ? [top[1], top[0], top[2]] : top;
  return (
    <div className="party-podium" data-testid="party-podium">
      {teams && teams.length ? (
        <div className="party-team-podium">
          {teams.map((team) => <div key={team.id} className={`party-team-final${team.rank === 1 ? ' winner' : ''}`} style={{ borderColor: team.color }}><span style={{ color: team.color }}>{team.name}</span><strong>{team.score}</strong>{team.rank === 1 ? <em>Winners!</em> : null}</div>)}
        </div>
      ) : null}
      <div className="party-podium-steps">
        {order.map((entry) => (
          <div key={entry.userId} className={`party-podium-step rank-${entry.rank}${winners.includes(entry.userId) ? ' winner' : ''}`}>
            <div className="party-podium-avatar"><UserAvatar userId={entry.userId} name={entry.name} size={entry.rank === 1 ? 64 : 48} />{entry.rank === 1 && winners.includes(entry.userId) ? <span className="party-crown" aria-hidden="true">👑</span> : null}</div>
            <div className="party-podium-name">{entry.name}</div>
            <div className="party-podium-score">{entry.score}</div>
            <div className="party-podium-block">{entry.rank}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Standings({ standings, meId }) {
  return (
    <ol className="party-standings" data-testid="party-standings">
      {standings.map((entry) => (
        <li key={entry.userId} className={entry.userId === meId ? 'me' : ''}>
          <span className="party-rank">#{entry.rank}</span>
          <UserAvatar userId={entry.userId} name={entry.name} size={26} />
          <span className="party-standing-name">{entry.name}{entry.left ? ' (left)' : ''}</span>
          <strong>{entry.score}</strong>
        </li>
      ))}
    </ol>
  );
}

function MyStats({ stats, meId }) {
  const mine = (stats || []).find((entry) => entry.userId === meId);
  if (!mine) return null;
  const items = [
    ['Drawings', mine.drawings],
    ['Correct guesses', mine.correctGuesses],
    ['Votes & reactions received', mine.votesReceived],
    ['Chains completed', mine.chains],
    ['Animation frames', mine.frames],
  ].filter(([, value]) => value > 0);
  if (!items.length) return null;
  return <div className="party-my-stats">{items.map(([label, value]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}</div>;
}

export default function PartyResults({ game, view, onLeave }) {
  const result = view.result;
  const isHost = view.me.isHost;
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  if (!result) return null;
  const players = view.game ? view.game.players : [];
  const saved = view.lastResult && view.lastResult.resultId;
  const iWon = result.winners.includes(view.me.userId);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(resultLink(saved));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  const toggleShare = async () => {
    const next = !shared;
    const response = await partyApi(`/api/party/results/${encodeURIComponent(saved)}/share`, { method: 'POST', body: { shared: next } });
    if (response.ok) setShared(next);
  };
  return (
    <div className="party-results" style={{ '--party-accent': view.mode.accent }} data-testid="party-results">
      {result.winners.length ? <Confetti /> : null}
      <header className="party-results-head">
        <div className="party-kicker">{view.mode.name}</div>
        <h2>{iWon ? 'You win! 🎉' : result.winners.length ? `${result.standings.filter((entry) => result.winners.includes(entry.userId)).map((entry) => entry.name).join(' & ')} win${result.winners.length === 1 ? 's' : ''}!` : (RESULT_TITLES[result.reason] || 'Game over')}</h2>
        {result.reason !== 'completed' ? <p className="party-muted">{RESULT_TITLES[result.reason]}</p> : null}
      </header>
      <Podium standings={result.standings} winners={result.winners} teams={result.teams} />
      <div className="party-results-grid">
        <section className="party-panel">
          <div className="party-panel-title">Final standings</div>
          <Standings standings={result.standings} meId={view.me.userId} />
          <MyStats stats={result.stats} meId={view.me.userId} />
        </section>
        <section className="party-panel actions">
          <div className="party-panel-title">What next?</div>
          {isHost ? (
            <div className="party-row wrap">
              <button type="button" className="party-btn big" onClick={() => game.act('rematch')} data-testid="party-rematch">Rematch</button>
              <button type="button" className="party-btn ghost" onClick={() => game.act('lobby')}>Change game</button>
            </div>
          ) : <p className="party-muted">The host can start a rematch or pick another game.</p>}
          <button type="button" className="party-btn ghost" onClick={onLeave}>Leave</button>
          {saved ? (
            <div className="party-share">
              <div className="party-muted">The gallery is saved for 30 days.</div>
              <div className="party-row wrap">
                <a className="party-btn small ghost" href={resultLink(saved)} target="_blank" rel="noreferrer">Open gallery</a>
                <button type="button" className="party-btn small ghost" onClick={copy}>{copied ? 'Copied!' : 'Copy link'}</button>
                {isHost ? <label className="party-inline"><input type="checkbox" checked={shared} onChange={toggleShare} /> Anyone signed in with the link can view</label> : null}
              </div>
            </div>
          ) : <div className="party-muted">{view.lastResult && view.lastResult.failed ? 'The gallery could not be saved.' : 'Saving the gallery…'}</div>}
        </section>
      </div>
      <section className="party-panel">
        <div className="party-panel-title">Gallery</div>
        <GameGallery gallery={result.gallery} store={game.store} players={players} teams={result.teams} />
      </section>
    </div>
  );
}

// The page behind a shared result link.
export function PartyResultPage({ resultId, meId, onBack }) {
  const [state, setState] = useState({ loading: true, result: null, error: '' });
  const store = useMemo(() => createDrawingStore(''), []);
  const [shared, setShared] = useState(false);
  useEffect(() => {
    let cancelled = false;
    partyApi(`/api/party/results/${encodeURIComponent(resultId)}`).then(({ ok, data }) => {
      if (cancelled) return;
      if (!ok) {
        setState({ loading: false, result: null, error: 'This gallery is private, expired or does not exist.' });
        return;
      }
      store.seed(data.result.drawings);
      setShared(Boolean(data.result.shared));
      setState({ loading: false, result: data.result, error: '' });
    });
    return () => {
      cancelled = true;
    };
  }, [resultId, store]);
  if (state.loading) return <div className="party-empty">Loading gallery…</div>;
  if (!state.result) return <div className="party-empty"><p>{state.error}</p><button type="button" className="party-btn" onClick={onBack}>Back to Party Games</button></div>;
  const result = state.result;
  const toggleShare = async () => {
    const response = await partyApi(`/api/party/results/${encodeURIComponent(resultId)}/share`, { method: 'POST', body: { shared: !shared } });
    if (response.ok) setShared(!shared);
  };
  return (
    <div className="party-results saved" data-testid="party-result-page">
      <header className="party-results-head">
        <button type="button" className="party-btn small ghost" onClick={onBack}>← Party Games</button>
        <div className="party-kicker">{result.modeName} · {new Date(result.finishedAt).toLocaleString()}</div>
        <h2>Game gallery</h2>
        {result.truncated ? <p className="party-muted">Some drawings were too large to keep.</p> : null}
        {result.canShare ? <label className="party-inline"><input type="checkbox" checked={shared} onChange={toggleShare} /> Anyone signed in with the link can view</label> : null}
      </header>
      <Podium standings={result.standings} winners={result.winners || []} teams={result.teams} />
      <section className="party-panel">
        <Standings standings={result.standings} meId={meId} />
      </section>
      <section className="party-panel">
        <GameGallery gallery={result.gallery} store={store} players={result.players} teams={result.teams} />
      </section>
    </div>
  );
}
